<template>
  <ModalCard
    v-show="open || url"
    :close-cb="closeModal"
    :open="open || !!url"
    :title="title"
  >
    <div
      v-if="getUrl"
      id="charteValidate"
      class="modal-card detailApplicationModalCard"
    >
      <header class="modal-card-head">
        <p
          class="modal-card-title"
          :aria-label="$t('applications.charte_header', applicationForCharteHeader)"
        >
          {{ $t("applications.charte_header", applicationForCharteHeader) }}
        </p>
      </header>
      <div class="modal-card-body">
        <CharteValidator :href="getUrl" />
      </div>
      <footer class="modal-card-foot">
        <form id="validateForm">
          <b-checkbox
            v-model="validated"
            class="validatesCheckbox"
            :aria-label="$t('applications.charte_message')"
            required
            type="primary"
          >
            {{ $t("applications.charte_message") }}
          </b-checkbox>
        </form>
      </footer>
    </div>
  </ModalCard>
</template>

<script lang="ts">
import ModalCard from "@/components/charts/ModalCard.vue";
import { useRedirections } from "@/composable/applications/useFunction";
import useText from "@/composable/components/text";
import CharteValidator from "@/components/application/CharteValidator.vue";
import { computed, PropType, ref } from "vue";
import services from "@/composable/services";
import { BCheckbox } from "buefy";
import "@/style/components/DetailApplicationModalCard.scss";
import { GenericLocalApplicationResult,
  getGenericApplicationId,
  getGenericApplicationLocalName,
  getGenericApplicationName } from "@/types/front/GenericLocalApplicationResult";
import { LocalApplicationLightResult } from "@/types/front/LocalApplicationLightResult";
import { buildCharteSignRequest } from "@/composable/applications/charteSignRequest";
import { isCharteSigned } from "@/composable/applications/charteSignState";

export default {
  name: "DetailApplicationModalCard",
  applicationRoles: {
    type: Object,
    default: () => {},
  },
  components: { BCheckbox, CharteValidator, ModalCard },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    application: {
      type: Object as PropType<GenericLocalApplicationResult>,
      required: true,
    },
    closeCb: {
      type: Function,
      required: false,
      default: () => {},
    },
    url: {
      required: true,
      type: String,
    },
    redirection: {
      required: true,
      type: Function,
    },
  },
  emits: ["setValidatedCharte"],
  setup(props, ctx) {
    const charteHREF = useText().refText;
    const getUrl = computed(() => charteHREF.value ? charteHREF.value : props.url);
    /**
     * Guard anti-double-clic sur la checkbox de validation de la charte .
     * Sans ce flag , le {@code set} de la computed {@code validated}
     * pouvait se déclencher 2 fois ( cf bug audit ) :
     *   1. l'appel à {@code modifAcount} n'était pas awaité ;
     *   2. la {@code get} re-lisait localStorage AVANT la mise à jour
     *      du localStorage par {@code updateAuthenticateduser} ;
     *   3. Vue ré-évaluait la checkbox à {@code false} → l'utilisateur
     *      recliquait , déclenchant une 2ème requête PUT /users avec
     *      potentiellement un identifiant différent ( fallback
     *      {@code id || applicationName} dans ApplicationInfoView ) ,
     *      générant un état {@code chartes: { name: ts1 , uuid: ts2 } }
     *      en base ( cf historique observé ) .
     */
    const isSigning = ref(false);

    /**
     * Signe la charte côté backend ( PUT /users avec charte = id app ) ,
     * attend la confirmation pour s'assurer que {@code localStorage} a
     * été mis à jour par {@code updateAuthenticateduser} , puis seulement
     * déclenche la redirection . Garantit que la {@code get} ci-dessus
     * reverra la nouvelle clé charte sans race .
     */
    async function signCharteAndRedirect(): Promise<void> {
      if (isSigning.value) return;
      isSigning.value = true;
      try {
        const stored = services.loginService.getStoredAuthenticatedUser() ?? null;
        const applicationId = getGenericApplicationId(props.application);
        const req = buildCharteSignRequest(stored, applicationId);
        await services.loginService.modifAcount(req);
        ctx.emit("setValidatedCharte", { applicationId, validated: true });
        props.redirection();
      }
      finally {
        isSigning.value = false;
      }
    }

    const validated = computed({
      get() {
        // Check de présence ( cf isCharteSigned ) ; l'ancien
        // {@code now > timestamp} était cassé par les skews
        // d'horloge client/serveur et forçait l'utilisateur à
        // signer 2 fois ( bug audit ) .
        return isCharteSigned(
          services.loginService.getStoredAuthenticatedUser(),
          getGenericApplicationId(props.application),
          getGenericApplicationName(props.application),
        );
      },
      set(bool) {
        // Setter purement déclencheur : délègue à {@link signCharteAndRedirect}
        // qui awaite la réponse backend avant la redirection , éliminant
        // la race localStorage évoquée plus haut .
        if (!bool) return;
        void signCharteAndRedirect();
      },
    });
    const { tmpCharte } = useRedirections(props.application);
    const title = computed(() => props.application && (getGenericApplicationLocalName(props.application) || getGenericApplicationName(props.application)));
    const applicationForCharteHeader
      = computed(() => props.application instanceof LocalApplicationLightResult ? props.application.application : props.application);
    const closeModal = () => {
      charteHREF.value = "";
      props.closeCb(); // exécute la callback utilisateur s’il y en a une
    };

    return {
      closeModal,
      charteHREF,
      getUrl,
      tmpCharte,
      validated,
      title,
      applicationForCharteHeader,
    };
  },
};
</script>
